import { z } from 'zod';
import { day, instant } from './primitives.ts';

const said = z.string();

/** A long share, 0 to 1, with the past year's band around it, ready for the tile's meter. */
const yearMeterSchema = z.object({
  low: z.number(),
  p10: z.number(),
  p90: z.number(),
  high: z.number(),
  now: z.number(),
  lowSaid: said,
  highSaid: said,
  label: said,
});

const holdingTileSchema = z.object({
  who: said,
  share: said,
  net: said,
  change: said.nullable(),
  expiry: z.boolean(),
  context: said.nullable(),
  meter: yearMeterSchema.nullable(),
});

const cashTileSchema = z.object({
  day: said,
  flows: z.object({ fii: said, dii: said }).nullable(),
  confirmed: z.object({ day: said, fpi: said, provisional: said.nullable() }).nullable(),
  month: z.object({ month: said, fii: said, dii: said, of: said.nullable() }).nullable(),
});

const sharePointSchema = z.object({
  day,
  said,
  /** Null where NSE published no file. */
  value: z.number().nullable(),
  expiry: z.boolean(),
  head: said.nullable(),
  parts: z.array(said),
});

const shareSeriesSchema = z.object({
  who: z.enum(['fii', 'client']),
  label: said,
  /** The middle eighty per cent of the past year, as long shares. */
  band: z.tuple([z.number(), z.number()]).nullable(),
  points: z.array(sharePointSchema),
});

const byIndexRowSchema = z.object({
  index: said,
  share: z.number(),
  shareSaid: said,
  oi: said,
  change: said,
});

const legSchema = z.object({
  net: said,
  /** The net against the largest in its column, −1 to 1. */
  bar: z.number(),
  under: said,
  change: said.nullable(),
});

const holdTableSchema = z.object({
  rows: z.array(z.object({ who: said, blurb: said, legs: z.array(legSchema) })),
  file: z.object({
    rows: z.array(z.object({ who: said, cells: z.array(said) })),
    total: z.array(said),
    link: z.string(),
  }),
});

const holdSessionSchema = z.object({
  day,
  said,
  expiry: z.boolean(),
  oi: holdTableSchema,
  traded: holdTableSchema.nullable(),
});

const flowPointSchema = z.object({
  said,
  fii: z.number().nullable(),
  dii: z.number().nullable(),
  fiiSaid: said.nullable(),
  diiSaid: said.nullable(),
  fpi: said.nullable(),
});

export const participantsSchema = z.object({
  newest: z
    .object({
      tiles: z.array(holdingTileSchema),
      cash: cashTileSchema,
      /** The session NSE has yet to publish, when the newest on file is the one before. */
      awaited: z.object({ latest: said, newest: said }).nullable(),
    })
    .nullable(),
  share: z.array(shareSeriesSchema),
  byIndex: z
    .object({ said, futures: z.array(byIndexRowSchema), options: z.array(byIndexRowSchema) })
    .nullable(),
  holds: z.object({
    legs: z.array(said),
    columns: z.array(said),
    sessions: z.array(holdSessionSchema),
  }),
  flows: z.object({ points: z.array(flowPointSchema), largest: said, scale: z.number() }),
  readAt: instant.nullable(),
});

export type ParticipantsRead = z.infer<typeof participantsSchema>;
